<template>
  <Teleport to="body">
    <div v-if="open" class="archive-browser" role="dialog" aria-modal="true">
      <button class="archive-browser__scrim" type="button" aria-label="Close archive browser" @click="$emit('close')"></button>
      <section class="archive-browser__panel">
        <header class="archive-browser__header">
          <div>
            <p class="archive-browser__eyebrow">Detached inventory</p>
            <h3>Archived grids</h3>
            <p>Browse detached grids and inspect their lamella history, including entries archived with the parent grid.</p>
          </div>
          <button class="archive-browser__close" type="button" @click="$emit('close')">Close</button>
        </header>

        <div class="archive-browser__toolbar">
          <input v-model="query" type="search" placeholder="Search grid or lamella name" />
          <span>{{ filteredGrids.length }} archived grid{{ filteredGrids.length === 1 ? "" : "s" }}</span>
        </div>

        <div v-if="filteredGrids.length" class="archive-browser__list">
          <article v-for="grid in filteredGrids" :key="grid.id" class="archive-card">
            <div class="archive-card__top">
              <div>
                <h4>{{ grid.name || "Untitled archived grid" }}</h4>
                <p>{{ formatDate(grid.dateModified || grid.dateCreated) }}</p>
              </div>
              <div class="archive-card__badges">
                <span>{{ getLamellaHistoryForGrid(grid.id).length }} lamella records</span>
                <span>{{ getLamellaHistoryMetrics(grid.id).archived }} archived with grid</span>
              </div>
            </div>

            <div v-if="getLamellaHistoryForGrid(grid.id).length" class="archive-card__lamellae">
              <button
                v-for="lamella in getLamellaHistoryForGrid(grid.id)"
                :key="lamella.id"
                type="button"
                class="archive-card__lamella"
                :class="{ 'archive-card__lamella--deleted': lamella.deletedAt }"
                @click="$emit('select-lamella', lamella.id)"
              >
                <strong>{{ lamella.name }}</strong>
                <span>{{ lamellaStatusLabel(lamella) }}</span>
              </button>
            </div>
            <div v-else class="archive-card__empty">No lamella history is attached to this archived grid.</div>
          </article>
        </div>

        <div v-else class="archive-browser__empty">No archived grids match this search.</div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref } from "vue";
import { useInventoryStore } from "../../../../stores/inventory";

defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["close", "select-lamella"]);

const inventoryStore = useInventoryStore();
const query = ref("");

const filteredGrids = computed(() => {
  const needle = query.value.trim().toLowerCase();
  const archived = inventoryStore.getArchivedGrids();
  if (!needle) return archived;
  return archived.filter((grid) => {
    const gridLabel = `${grid.name || ""} ${grid.description || ""}`.toLowerCase();
    const lamellaLabel = getLamellaHistoryForGrid(grid.id)
      .map((lamella) => `${lamella.name} ${lamella.currentStage || ""} ${lamella.status || ""}`.toLowerCase())
      .join(" ");
    return gridLabel.includes(needle) || lamellaLabel.includes(needle);
  });
});

function getLamellaHistoryForGrid(gridId) {
  return Array.from(inventoryStore.lamellae.values())
    .filter((lamella) => lamella.gridId === gridId)
    .sort((left, right) => new Date(right.dateModified || right.dateCreated) - new Date(left.dateModified || left.dateCreated));
}

function getLamellaHistoryMetrics(gridId) {
  return getLamellaHistoryForGrid(gridId).reduce(
    (summary, lamella) => {
      summary.images += lamella.imageRefs?.length || 0;
      if (lamella.deletedAt) summary.archived += 1;
      return summary;
    },
    { archived: 0, images: 0 },
  );
}

function lamellaStatusLabel(lamella) {
  if (lamella.deletedByParentCascade) {
    return "Archived with parent grid";
  }
  if (lamella.deletedAt) {
    return "Archived separately";
  }
  return lamella.currentStage || lamella.status || "No status";
}

function formatDate(value) {
  if (!value) return "No timestamp";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}
</script>

<style scoped>
.archive-browser {
  position: fixed;
  inset: 0;
  z-index: 150;
}
.archive-browser__scrim {
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(15, 23, 42, 0.72);
}
.archive-browser__panel {
  position: absolute;
  inset: 2rem;
  background: linear-gradient(180deg, #fffaf0 0%, #fff 100%);
  border-radius: 1.5rem;
  border: 1px solid rgba(217, 119, 6, 0.2);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.archive-browser__header,
.archive-browser__toolbar {
  padding: 1rem 1.2rem;
  border-bottom: 1px solid rgba(251, 191, 36, 0.25);
}
.archive-browser__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
.archive-browser__header h3 {
  margin: 0.2rem 0 0.25rem;
  font-size: 1.4rem;
  color: #7c2d12;
}
.archive-browser__header p {
  margin: 0;
  color: #9a3412;
}
.archive-browser__eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.68rem;
  color: #d97706;
}
.archive-browser__close {
  align-self: flex-start;
  border-radius: 999px;
  border: 1px solid rgba(217, 119, 6, 0.35);
  background: white;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
}
.archive-browser__toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}
.archive-browser__toolbar input {
  flex: 1;
  min-width: 0;
  background: white;
}
.archive-browser__toolbar span {
  color: #92400e;
  font-size: 0.82rem;
  font-weight: 700;
}
.archive-browser__list {
  overflow: auto;
  padding: 1rem 1.2rem 1.2rem;
  display: grid;
  gap: 0.9rem;
}
.archive-card {
  border: 1px solid rgba(217, 119, 6, 0.18);
  border-radius: 1.15rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.9);
}
.archive-card__top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
.archive-card__top h4 {
  margin: 0;
  color: #111827;
}
.archive-card__top p {
  margin: 0.25rem 0 0;
  color: #6b7280;
}
.archive-card__badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.archive-card__badges span {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #ffedd5;
  color: #9a3412;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 0.22rem 0.6rem;
}
.archive-card__lamellae {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.9rem;
}
.archive-card__lamella {
  text-align: left;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #f8fafc;
  border-radius: 0.95rem;
  padding: 0.7rem 0.8rem;
}
.archive-card__lamella strong,
.archive-card__lamella span {
  display: block;
}
.archive-card__lamella span {
  color: #64748b;
  font-size: 0.78rem;
  margin-top: 0.2rem;
}
.archive-card__lamella--deleted {
  border-style: dashed;
  background: #fff7ed;
}
.archive-card__empty,
.archive-browser__empty {
  margin: 1rem 1.2rem;
  border: 1px dashed rgba(217, 119, 6, 0.35);
  border-radius: 1rem;
  padding: 1rem;
  color: #92400e;
  background: rgba(255, 251, 235, 0.72);
}
@media (max-width: 900px) {
  .archive-browser__panel {
    inset: 0;
    border-radius: 0;
  }
  .archive-browser__header,
  .archive-browser__toolbar,
  .archive-card__top {
    flex-direction: column;
  }
}
</style>
